import React, { useMemo, useCallback } from 'react';
import { useFormikContext, getIn } from 'formik';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { TrashIcon } from 'components/icons/Icons';
import { Button } from 'components/ui/Button';
import { SupportingFile } from 'shared/types/optim/supportingFiles';
import { FileTypeEnum } from 'api/documents';
import { filesAPI } from 'services/swagger-api';
import {
  GenericDocumentsManager,
  DocumentSection,
  DocumentItem
} from 'components/domain/Document/GenericDocumentManager';
import { DocumentSectionsUploaderConfig } from 'types/form/componentConfig';
import { Eye } from 'lucide-react';
import { useDocumentViewerDialog } from 'components/domain/Document/useDocumentViewerDialog';
import { IdentityDocumentUploader } from 'components/domain/IdentityUpload';
import { DocumentVerificationStatusEnum } from 'api/actor';

interface FormikMultiSectionDocumentUploaderProps extends Omit<DocumentSectionsUploaderConfig, 'componentKey' | 'name'> {
  disabled?: boolean;
}

export const FormikDocumentSectionUploader: React.FC<FormikMultiSectionDocumentUploaderProps> = ({
  label,
  sections: sectionsConfig,
  completedSectionLabel = 'Dossier complet',
  disabledSectionLabel = 'Section non disponible',
  emptyMessage = 'Aucun document requis',
  acceptedFormats = '.pdf,.doc,.docx,.jpg,.jpeg,.png',
  disabled = false,
}) => {
  const { setFieldValue, values } = useFormikContext();
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);
  const { openDocumentViewer } = useDocumentViewerDialog();

  const handleUpload = useCallback(async (sectionId: string, file: File, fileType: FileTypeEnum): Promise<void> => {
    try {
      const section = sectionsConfig.find(s => s.id === sectionId);
      if (!section) return;

      const document = section.documents.find(d => d.fileType === fileType);
      if (!document) return;

      if (document.entityId) {
        const response = await filesAPI.uploadFile({ fileType, file, context: document.context, objetId: document.entityId });

        if (response.isSuccess) {
          console.log(values)
          const currentValue = getIn(values, document.fieldName) as SupportingFile | undefined;
          const [parentKey, childKey] = document.fieldName.split('.');
          setFieldValue(parentKey, {
            ...(values as any)[parentKey],
            [childKey]: {
              ...currentValue,
              file,
              storedFileId: response.id,
              fileName: file.name,
            }
          });
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }, [sectionsConfig, setFieldValue, values]);

  const handleRemove = useCallback(async (sectionId: string, storedFileId: string): Promise<void> => {
    try {
      const section = sectionsConfig.find(s => s.id === sectionId);
      if (!section) return;

      for (const doc of section.documents) {
        const value = getIn(values, doc.fieldName) as SupportingFile | undefined;
        const currentStoredId = value?.storedFileId
        if (currentStoredId === storedFileId) {
          const response = await filesAPI.deleteFileByStoredFileId({
            storedFileId,
          });

          if (response.isSuccess) {
            setFieldValue(doc.fieldName, undefined);
          }
        }
      }
    } catch (error) {
      console.error('Error removing file:', error);
      throw error;
    }
  }, [sectionsConfig, setFieldValue, values]);

  const handleViewDocument = useCallback((storedFileId: string, documentName: string) => {
    if (storedFileId) {
      openDocumentViewer({
        title: documentName,
        storedFileId,
        showDefaultActions: true
      });
    }
  }, [openDocumentViewer]);

  const renderUploadedActions = useCallback((
    storedFileId: string,
    onRemove: () => void,
    _sectionId: string,
    document: any
  ) => {
    return (
      <div className="flex items-center gap-1">
        <Button
          type="button"
          plain
          onClick={() => handleViewDocument(storedFileId, document.name)}
          title="Visualiser le document"
        >
          <Eye className="w-4 h-4 text-gray-500 hover:text-primary" />
        </Button>
        {onRemove && (
          <Button
            type="button"
            plain
            onClick={onRemove}
            disabled={disabled}
            title="Supprimer le document"
          >
            <TrashIcon className="w-4 h-4 text-gray-500 hover:text-red-500" />
          </Button>
        )}
      </div>
    );
  }, [disabled, handleViewDocument]);

  const isDocumentCompleted = useCallback(
    (fileType: FileTypeEnum, value: SupportingFile | undefined): boolean => {
      if (!value?.storedFileId) return false;
      if (fileType === FileTypeEnum.PieceIdentite) {
        return value.verificationStatus === DocumentVerificationStatusEnum.Verified;
      }
      return true;
    },
    []
  );

  const sections: DocumentSection[] = useMemo(() => {
    return sectionsConfig.map((sectionConfig) => {
      const isEligible = typeof sectionConfig.isEligible === 'function'
        ? sectionConfig.isEligible(values)
        : sectionConfig.isEligible !== false;

      const documents = sectionConfig.documents.map((doc) => {
        const value = getIn(values, doc.fieldName) as SupportingFile | undefined;
        return {
          fileType: doc.fileType,
          name: t(doc.name),
          storedFileId: value?.storedFileId,
        };
      });

      const completedCount = sectionConfig.documents.reduce((acc, doc) => {
        const value = getIn(values, doc.fieldName) as SupportingFile | undefined;
        return isDocumentCompleted(doc.fileType, value) ? acc + 1 : acc;
      }, 0);
      const isCompleted = completedCount === documents.length && documents.length > 0;

      return {
        id: sectionConfig.id,
        title: t(sectionConfig.label),
        icon: sectionConfig.icon,
        documents,
        isEligible,
        isCompleted,
      };
    });
  }, [sectionsConfig, values, t, isDocumentCompleted]);

  const renderCustomDocumentItem = useCallback(
    (document: DocumentItem, sectionId: string) => {
      if (document.fileType !== FileTypeEnum.PieceIdentite) return null;

      const section = sectionsConfig.find(s => s.id === sectionId);
      const docConfig = section?.documents.find(
        d => d.fileType === FileTypeEnum.PieceIdentite
      );
      if (!docConfig?.entityId) return null;

      const currentValue = getIn(values, docConfig.fieldName) as SupportingFile | undefined;

      const handleSuccess = (newStoredFileId: string) => {
        setFieldValue(docConfig.fieldName, {
          ...(currentValue || {}),
          storedFileId: newStoredFileId,
          fileType: document.fileType,
          fileName: document.name,
          verificationStatus: DocumentVerificationStatusEnum.Verified
        });
      };

      const handleRemove = async () => {
        if (document.storedFileId) {
          await filesAPI.deleteFileByStoredFileId({ storedFileId: document.storedFileId });
          setFieldValue(docConfig.fieldName, undefined);
        }
      };

      return (
        <IdentityDocumentUploader
          key={`${sectionId}-${document.fileType}`}
          documentName={document.name}
          naturalPersonId={docConfig.entityId}
          storedFileId={document.storedFileId}
          verificationStatus={currentValue?.verificationStatus}
          onSuccess={handleSuccess}
          onRemove={handleRemove}
          disabled={disabled}
        />
      );
    },
    [sectionsConfig, setFieldValue, values, disabled]
  );

  return (
    <div>
      {label && <h3 className="text-lg font-semibold mb-4">{t(label)}</h3>}
      <GenericDocumentsManager
        sections={sections}
        onUpload={handleUpload}
        onRemove={handleRemove}
        completedSectionLabel={completedSectionLabel}
        disabledSectionLabel={disabledSectionLabel}
        emptyMessage={emptyMessage}
        renderUploadedActions={renderUploadedActions}
        renderCustomDocumentItem={renderCustomDocumentItem}
        acceptedFormats={acceptedFormats}
        disabled={disabled}
      />
    </div>
  );
};
